import React, { useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";

/* -----------------------------
   CATEGORY GENERATORS
--------------------------------*/
const getWeeklyLabelsDynamic = (weeklyDataLength) => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const today = new Date();
  const dayOfWeek = (today.getDay() + 6) % 7; // Mon=0 ... Sun=6

  const startIndex = dayOfWeek - (weeklyDataLength - 1);
  return days.slice(startIndex <= 0 ? 0 : startIndex, dayOfWeek + 1);
};

const getMonthlyLabels = (monthlyDataLength) => {
  const labels = [];
  const now = new Date();
  for (let i = monthlyDataLength - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    labels.push(d.toLocaleString("default", { month: "short", year: "numeric" }));
  }
  return labels;
};

const getYearlyLabels = (yearlyDataLength) => {
  const labels = [];
  const currentYear = new Date().getFullYear();
  for (let i = yearlyDataLength - 1; i >= 0; i--) {
    labels.push((currentYear - i).toString());
  }
  return labels;
};

/* -----------------------------
   DATA NORMALIZER
--------------------------------*/
const normalizeData = (data = [], length) => {
  const padded = Array(length).fill(0);
  data.slice(-length).forEach((val, idx) => {
    padded[length - data.length + idx] = val;
  });
  return padded;
};

/* -----------------------------
   COMPONENT
--------------------------------*/
const GeneratedUserContent = ({ dashboardData }) => {
  const [timeframe, setTimeframe] = useState("monthly");

  const categories = useMemo(() => {
    const dataSample =
      dashboardData?.chartData?.users?.[timeframe] || [];

    if (timeframe === "weekly") return getWeeklyLabelsDynamic(dataSample.length);
    if (timeframe === "yearly") return getYearlyLabels(dataSample.length);
    return getMonthlyLabels(dataSample.length);
  }, [timeframe, dashboardData]);

  const categoryLength = categories.length;

  const userStatusChartSeries = useMemo(() => {
    const series = [];

    const addSeries = (label, dataPath) => {
      const data = dashboardData?.chartData?.[dataPath]?.[timeframe];
      if (data) {
        series.push({
          name: label,
          data: normalizeData(data, categoryLength),
        });
      }
    };

    addSeries("Total Users", "users");
    addSeries("Active Users", "activeUsers");
    addSeries("New Sign-ups", "newSignUps");
    addSeries("Total Compatibilities", "totalCompatibilities");
    addSeries("Matched Compatibilities", "matchedCompatibilities");

    return series;
  }, [dashboardData, timeframe, categoryLength]);

  if (!userStatusChartSeries.length) return null;

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    theme: { mode: "light" },
    xaxis: { categories },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "65%",
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px" },
    },
    tooltip: { enabled: true },
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">Users & Compatibility Metrics</h6>

          <select
            className="form-select form-select-sm w-auto"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>

        <ReactApexChart
          options={chartOptions}
          series={userStatusChartSeries}
          type="bar"
          height={320}
        />
      </div>
    </div>
  );
};

export default GeneratedUserContent;
